import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff, Lock, Mail, User, CreditCard, ShieldCheck, Sparkles, Crown } from "lucide-react";
import Logo from "../components/Logo";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import { useAuth, formatApiError } from "../lib/auth";

const planMeta = {
  pro: { name: "Pro Plan", price: "$199/month", color: "#1D6AFF", icon: Sparkles },
  pro_3m: { name: "Pro Plan · 3-month", price: "$199/month", color: "#1D6AFF", icon: Sparkles },
  pro_12m: { name: "Pro Plan · 12-month", price: "$179/month", color: "#1D6AFF", icon: Sparkles },
  elite: { name: "Elite Plan", price: "$397/month", color: "#D97706", icon: Crown },
  elite_3m: { name: "Elite Plan · 3-month", price: "$299/month", color: "#D97706", icon: Crown },
  elite_12m: { name: "Elite Plan · 12-month", price: "$279/month", color: "#D97706", icon: Crown },
};

export default function Signup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signup, user, logout } = useAuth();
  const selectedPlan = params.get("plan");
  const plan = planMeta[selectedPlan] || null;
  const PlanIcon = plan?.icon;

  const [step, setStep] = useState("form");
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [card, setCard] = useState({ number: "", exp: "", cvv: "", holder: "" });
  const [errors, setErrors] = useState({});
  const [processing, setProcessing] = useState(false);

  // If the user is already authenticated (e.g. came back from canceled Stripe)
  // and a plan is selected, jump straight to payment.
  useEffect(() => {
    window.scrollTo(0, 0);
    if (user && selectedPlan && plan) {
      setForm((f) => ({ ...f, name: user.name, email: user.email }));
      setStep("payment");
    }
  }, [user, selectedPlan, plan]);

  const fmtCard = (v) => v.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
  const fmtExp = (v) => {
    const d = v.replace(/\D/g, "").slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  };

  const validateForm = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (form.password.length < 6) e.password = "Min 6 characters";
    if (form.password !== form.confirm) e.confirm = "Passwords don't match";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateCard = () => {
    const e = {};
    if (card.number.replace(/\s/g, "").length < 12) e.number = "Enter a valid card number";
    if (!/^\d{2}\/\d{2}$/.test(card.exp)) e.exp = "MM/YY";
    if (!/^\d{3,4}$/.test(card.cvv)) e.cvv = "3–4 digits";
    if (!card.holder.trim()) e.holder = "Name required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submitForm = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setProcessing(true);
    try {
      await signup({ name: form.name, email: form.email, password: form.password });
      if (selectedPlan && plan) {
        setStep("payment");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setErrors({ general: formatApiError(err, "Could not create account.") });
    } finally {
      setProcessing(false);
    }
  };

  const submitPayment = async (e) => {
    e?.preventDefault();
    if (!selectedPlan) return;
    setProcessing(true);
    setErrors({});
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/checkout/session`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan_id: selectedPlan,
          origin_url: window.location.origin,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Could not start checkout");
      }
      const data = await res.json();
      if (!data.url) throw new Error("No checkout URL returned");
      window.location.href = data.url;
    } catch (err) {
      setProcessing(false);
      setErrors({ general: err.message || "Payment could not be started. Try again." });
    }
  };

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/dashboard${selectedPlan ? `?plan=${selectedPlan}` : ""}`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 relative overflow-hidden" data-testid="signup-page">
      <div className="absolute inset-0 bg-grid mask-radial-fade opacity-30 pointer-events-none" />
      <div className="absolute -top-32 -left-32 w-[480px] h-[480px] bg-[#1D6AFF]/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] bg-[#1D6AFF]/8 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative max-w-md mx-auto px-5 pt-10 pb-16">
        <button
          data-testid="signup-back"
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </button>

        <div className="flex justify-center mb-7">
          <Logo size="lg" />
        </div>

        {plan && (
          <div
            data-testid="selected-plan-banner"
            className="bg-white rounded-2xl px-5 py-4 mb-6 flex items-center justify-between border shadow-sm"
            style={{ borderColor: `${plan.color}55` }}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${plan.color}18`, border: `1px solid ${plan.color}40` }}>
                {PlanIcon && <PlanIcon className="w-4 h-4" style={{ color: plan.color }} />}
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">You selected</div>
                <div className="text-sm font-semibold" style={{ color: plan.color }}>{plan.name} · {plan.price}</div>
              </div>
            </div>
          </div>
        )}

        {step === "form" && (
          <div className="bg-white border border-slate-200 rounded-3xl p-7 sm:p-8 animate-fade-up shadow-[0_20px_50px_-15px_rgba(15,23,42,0.12)]">
            <h1 className="font-display text-3xl font-bold mb-2 text-slate-900">Create your account</h1>
            <p className="text-sm text-slate-500 mb-7">Start closing more deals in under 5 minutes.</p>

            <button
              data-testid="google-signup-btn"
              onClick={googleSignup}
              className="w-full flex items-center justify-center gap-3 bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 font-semibold rounded-xl py-3.5 transition-all mb-5"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden>
                <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.45c-.28 1.46-1.13 2.7-2.4 3.53v2.93h3.88c2.27-2.09 3.56-5.17 3.56-8.7z" />
                <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.93l-3.88-2.93c-1.07.73-2.45 1.16-4.05 1.16-3.11 0-5.75-2.1-6.69-4.93H1.3v3.09C3.28 21.31 7.31 24 12 24z" />
                <path fill="#FBBC05" d="M5.31 14.37c-.24-.73-.38-1.5-.38-2.37s.14-1.64.38-2.37V6.54H1.3C.47 8.18 0 10.04 0 12s.47 3.82 1.3 5.46l4.01-3.09z" />
                <path fill="#EA4335" d="M12 4.75c1.76 0 3.34.61 4.59 1.79l3.43-3.43C17.95 1.13 15.24 0 12 0 7.31 0 3.28 2.69 1.3 6.54l4.01 3.09C6.25 6.85 8.89 4.75 12 4.75z" />
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-500 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            <form onSubmit={submitForm} className="space-y-4">
              <Field testId="signup-name" icon={User} label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} error={errors.name} placeholder="Jane Doe" />
              <Field testId="signup-email" icon={Mail} label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} error={errors.email} placeholder="jane@agency.com" />
              <Field
                testId="signup-password"
                icon={Lock}
                label="Password"
                type={showPw ? "text" : "password"}
                value={form.password}
                onChange={(v) => setForm({ ...form, password: v })}
                error={errors.password}
                placeholder="••••••••"
                rightAction={
                  <button type="button" onClick={() => setShowPw(!showPw)} className="text-slate-400 hover:text-slate-700">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                }
              />
              <PasswordStrengthMeter password={form.password} />
              <Field testId="signup-confirm" icon={Lock} label="Confirm Password" type={showPw ? "text" : "password"} value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} error={errors.confirm} placeholder="••••••••" />

              {errors.general && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-testid="signup-error">{errors.general}</div>
              )}

              <button
                type="submit"
                data-testid="signup-submit"
                disabled={processing}
                className="w-full bg-[#1D6AFF] hover:bg-[#1552C6] disabled:opacity-70 text-white font-semibold rounded-xl py-3.5 transition-all shadow-[0_14px_30px_-8px_rgba(29,106,255,0.4)]"
              >
                {processing ? "Creating account…" : (selectedPlan ? "Continue to payment" : "Create free account")}
              </button>

              <p className="text-xs text-slate-500 text-center leading-relaxed" data-testid="signup-legal-consent">
                By creating an account, you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#1D6AFF] hover:underline">Terms of Service</a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#1D6AFF] hover:underline">Privacy Policy</a>.
              </p>
            </form>

            <div className="text-center text-sm text-slate-500 mt-6">
              Already have an account?{" "}
              <button
                data-testid="signup-login-link"
                onClick={async () => {
                  // If a previous user (e.g., the device's last admin session) is still
                  // signed in via cookie, clear it first so /login renders the actual form
                  // instead of auto-redirecting them straight to the dashboard.
                  if (user) {
                    try { await logout(); } catch (_e) { /* ignore */ }
                  }
                  navigate(selectedPlan ? `/login?plan=${selectedPlan}` : "/login");
                }}
                className="text-[#1D6AFF] hover:underline font-medium"
              >
                Log in
              </button>
            </div>
          </div>
        )}

        {step === "payment" && plan && (
          <div className="bg-white border border-slate-200 rounded-3xl p-7 sm:p-8 animate-fade-up shadow-[0_20px_50px_-15px_rgba(15,23,42,0.12)]" data-testid="payment-step">
            <h1 className="font-display text-3xl font-bold mb-2 text-slate-900">Complete your purchase</h1>
            <p className="text-sm text-slate-500 mb-7">You&apos;ll be redirected to Stripe&apos;s secure checkout to finish payment.</p>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mb-5 flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider">{plan.name}</div>
                <div className="font-display text-2xl font-bold text-slate-900 mt-0.5">{plan.price}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Billed via</div>
                <div className="text-sm font-semibold text-[#635BFF]">Stripe</div>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm mb-5">
              <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
              <span className="text-emerald-800">PCI-DSS compliant. Cancel anytime.</span>
            </div>

            {errors.general && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{errors.general}</div>
            )}

            <button
              type="button"
              onClick={submitPayment}
              disabled={processing}
              data-testid="complete-purchase-btn"
              className="w-full bg-[#1D6AFF] hover:bg-[#1552C6] disabled:opacity-70 text-white font-semibold rounded-xl py-3.5 transition-all shadow-[0_14px_30px_-8px_rgba(29,106,255,0.4)] flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  Continue to secure payment
                </>
              )}
            </button>
            <p className="text-[11px] text-slate-400 text-center mt-3">
              You'll be redirected to Stripe to complete your payment securely.
            </p>

            <button
              type="button"
              data-testid="payment-back-btn"
              onClick={() => setStep("form")}
              className="w-full text-sm text-slate-500 hover:text-slate-900 transition-colors pt-3"
            >
              ← Back to account details
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ testId, icon: Icon, label, value, onChange, type = "text", placeholder, error, rightAction }) => (
  <div>
    <label className="block text-xs font-medium text-slate-500 mb-1.5 uppercase tracking-wider">{label}</label>
    <div className={`relative flex items-center bg-slate-50 border ${error ? "border-red-400" : "border-slate-200"} rounded-xl px-3.5 py-3 focus-within:border-[#1D6AFF] focus-within:bg-white transition-all`}>
      {Icon && <Icon className="w-4 h-4 text-slate-400 mr-2.5 shrink-0" />}
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-sm"
      />
      {rightAction}
    </div>
    {error && <div className="text-xs text-red-500 mt-1.5">{error}</div>}
  </div>
);
